import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import { t } from '../../hooks/useT';
import {
  buildAllLevelRects,
  clampRect,
  computeAutoCropRect,
  rectToPayload,
  type CropMeta,
  type CropRect,
} from '../../utils/cropUtils';

interface ZoomLevelData {
  level: number;
  imageUrl: string;
  cropMeta?: CropMeta;
}

interface CropEditorData {
  puzzleId: string;
  imageVersion: number;
  originalImageUrl: string;
  width: number;
  height: number;
  zoomLevels: ZoomLevelData[];
}

interface Props {
  puzzleId: string;
  onClose: () => void;
  onApplied: (puzzle: { id: string; zoomLevels: ZoomLevelData[]; imageVersion?: number }) => void;
}

type DragMode = 'move' | 'resize-se' | null;

function initialFocal(zoomLevels: ZoomLevelData[]) {
  const meta = zoomLevels[0]?.cropMeta;
  if (meta?.focalX != null && meta?.focalY != null) {
    return { focalX: meta.focalX, focalY: meta.focalY };
  }
  return { focalX: 0.5, focalY: 0.5 };
}

function initialCustom(zoomLevels: ZoomLevelData[]): Partial<Record<number, CropRect>> {
  const custom: Partial<Record<number, CropRect>> = {};
  for (const z of zoomLevels) {
    if (z.cropMeta) {
      custom[z.level] = {
        left: z.cropMeta.left,
        top: z.cropMeta.top,
        width: z.cropMeta.width,
        height: z.cropMeta.height,
      };
    }
  }
  return custom;
}

export function AdminCropEditor({ puzzleId, onClose, onApplied }: Props) {
  const [data, setData] = useState<CropEditorData | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [focalX, setFocalX] = useState(0.5);
  const [focalY, setFocalY] = useState(0.5);
  const [customLevels, setCustomLevels] = useState<Partial<Record<number, CropRect>>>({});
  const [activeLevel, setActiveLevel] = useState(0);
  const [displayScale, setDisplayScale] = useState(1);
  const [dragMode, setDragMode] = useState<DragMode>(null);
  const dragStart = useRef<{ x: number; y: number; rect: CropRect } | null>(null);
  const dragModeRef = useRef<DragMode>(null);
  const displayScaleRef = useRef(1);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setError('');
    api<CropEditorData>(`/admin/puzzles/${puzzleId}/crop-editor`)
      .then((res) => {
        setData(res);
        const focal = initialFocal(res.zoomLevels);
        setFocalX(focal.focalX);
        setFocalY(focal.focalY);
        setCustomLevels(initialCustom(res.zoomLevels));
      })
      .catch(() => setError(t('admin.crops.loadError')));
  }, [puzzleId]);

  const levelRects = useMemo(() => {
    if (!data) return {};
    return buildAllLevelRects(data.width, data.height, focalX, focalY, customLevels);
  }, [data, focalX, focalY, customLevels]);

  const activeRect = levelRects[activeLevel];

  const updateDisplayScale = useCallback(() => {
    if (!stageRef.current || !data) return;
    const w = stageRef.current.clientWidth;
    const scale = w / data.width;
    displayScaleRef.current = scale;
    setDisplayScale(scale);
  }, [data]);

  useEffect(() => {
    updateDisplayScale();
    window.addEventListener('resize', updateDisplayScale);
    return () => window.removeEventListener('resize', updateDisplayScale);
  }, [updateDisplayScale, data]);

  const setLevelRect = useCallback((level: number, rect: CropRect) => {
    if (!data) return;
    setCustomLevels((prev) => ({
      ...prev,
      [level]: clampRect(rect, data.width, data.height),
    }));
  }, [data]);

  useEffect(() => {
    dragModeRef.current = dragMode;
  }, [dragMode]);

  useEffect(() => {
    if (!dragMode || !data) return;

    const handlePointerMove = (e: PointerEvent) => {
      const start = dragStart.current;
      const mode = dragModeRef.current;
      if (!start || !mode) return;

      const scale = displayScaleRef.current || 1;
      const dx = (e.clientX - start.x) / scale;
      const dy = (e.clientY - start.y) / scale;

      if (mode === 'move') {
        setLevelRect(activeLevel, {
          ...start.rect,
          left: start.rect.left + dx,
          top: start.rect.top + dy,
        });
      } else if (mode === 'resize-se') {
        setLevelRect(activeLevel, {
          ...start.rect,
          width: start.rect.width + dx,
          height: start.rect.height + dy,
        });
      }
    };

    const endDrag = () => {
      setDragMode(null);
      dragModeRef.current = null;
      dragStart.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    };
  }, [dragMode, data, activeLevel, setLevelRect]);

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!data || dragMode) return;
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e.clientX - rect.left) / displayScale;
    const y = (e.clientY - rect.top) / displayScale;
    const fx = Math.max(0, Math.min(1, x / data.width));
    const fy = Math.max(0, Math.min(1, y / data.height));
    setFocalX(fx);
    setFocalY(fy);
    setCustomLevels({});
  };

  const onPointerDown = (e: React.PointerEvent, mode: DragMode) => {
    if (!activeRect) return;
    e.preventDefault();
    e.stopPropagation();
    setDragMode(mode);
    dragModeRef.current = mode;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      rect: { ...activeRect },
    };
  };

  const handleAutoFocal = () => {
    setCustomLevels({});
  };

  const handleResetLevel = () => {
    if (!data) return;
    const auto = computeAutoCropRect(activeLevel, data.width, data.height, focalX, focalY);
    setLevelRect(activeLevel, auto);
  };

  const handleApply = async () => {
    if (!data) return;
    setSaving(true);
    setError('');
    try {
      const res = await api<{ puzzle: { id: string; zoomLevels: ZoomLevelData[]; imageVersion?: number } }>(`/admin/puzzles/${puzzleId}/crops`, {
        method: 'POST',
        body: JSON.stringify({
          focalX,
          focalY,
          levels: rectToPayload(levelRects),
        }),
      });
      onApplied(res.puzzle);
      onClose();
    } catch {
      setError(t('admin.crops.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (error && !data) {
    return (
      <div className="admin-crop-overlay" role="dialog" aria-modal="true">
        <div className="admin-crop-modal">
          <p className="admin-msg">{error}</p>
          <button type="button" className="btn secondary" onClick={onClose}>{t('admin.crops.cancel')}</button>
        </div>
      </div>
    );
  }

  if (!data || !activeRect) {
    return (
      <div className="admin-crop-overlay" role="dialog" aria-modal="true">
        <div className="admin-crop-modal">
          <p className="admin-msg">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const overlayStyle = {
    left: `${activeRect.left * displayScale}px`,
    top: `${activeRect.top * displayScale}px`,
    width: `${activeRect.width * displayScale}px`,
    height: `${activeRect.height * displayScale}px`,
  };

  const focalStyle = {
    left: `${focalX * data.width * displayScale}px`,
    top: `${focalY * data.height * displayScale}px`,
  };

  return (
    <div className="admin-crop-overlay" role="dialog" aria-modal="true">
      <div className="admin-crop-modal">
        <header className="admin-crop-head">
          <h2>{t('admin.crops.title')}</h2>
          <button type="button" className="admin-crop-close" onClick={onClose} aria-label={t('admin.crops.cancel')}>×</button>
        </header>

        <p className="admin-crop-hint">{t('admin.crops.focalHint')}</p>

        <div className="admin-crop-level-tabs">
          {[0, 1, 2, 3, 4].map((lvl) => (
            <button
              key={lvl}
              type="button"
              className={`admin-zoom-btn admin-zoom-btn--compact${activeLevel === lvl ? ' active' : ''}`}
              onClick={() => setActiveLevel(lvl)}
            >
              {lvl}
            </button>
          ))}
        </div>

        <div
          ref={stageRef}
          className="admin-crop-stage"
          onClick={handleImageClick}
        >
          <img
            src={data.originalImageUrl}
            alt=""
            draggable={false}
            onLoad={updateDisplayScale}
          />
          <div className="admin-crop-focal" style={focalStyle} aria-hidden />
          <div
            className="admin-crop-rect"
            style={overlayStyle}
            onPointerDown={(e) => onPointerDown(e, 'move')}
          >
            <span className="admin-crop-rect-label">{activeLevel}</span>
            <span
              className="admin-crop-handle admin-crop-handle--se"
              onPointerDown={(e) => onPointerDown(e, 'resize-se')}
            />
          </div>
        </div>

        <div className="admin-crop-previews">
          {[0, 1, 2, 3, 4].map((lvl) => {
            const r = levelRects[lvl];
            const bgW = data.width / r.width * 100;
            const bgH = data.height / r.height * 100;
            const posX = (r.left / (data.width - r.width)) * 100 || 0;
            const posY = (r.top / (data.height - r.height)) * 100 || 0;
            return (
              <button
                key={lvl}
                type="button"
                className={`admin-crop-preview${activeLevel === lvl ? ' active' : ''}`}
                onClick={() => setActiveLevel(lvl)}
              >
                <div
                  className="admin-crop-preview-inner"
                  style={{
                    backgroundImage: `url(${data.originalImageUrl})`,
                    backgroundSize: `${bgW}% ${bgH}%`,
                    backgroundPosition: `${Number.isFinite(posX) ? posX : 50}% ${Number.isFinite(posY) ? posY : 50}%`,
                  }}
                />
                <span>{lvl}</span>
              </button>
            );
          })}
        </div>

        <div className="admin-crop-actions">
          <button type="button" className="btn secondary admin-btn-sm" onClick={handleAutoFocal}>
            {t('admin.crops.autoFocal')}
          </button>
          <button type="button" className="btn secondary admin-btn-sm" onClick={handleResetLevel}>
            {t('admin.crops.resetLevel')}
          </button>
          <button type="button" className="btn primary" disabled={saving} onClick={() => void handleApply()}>
            {saving ? t('common.loading') : t('admin.crops.apply')}
          </button>
          <button type="button" className="btn secondary" onClick={onClose}>
            {t('admin.crops.cancel')}
          </button>
        </div>

        {error && <p className="admin-msg admin-msg-error">{error}</p>}
      </div>
    </div>
  );
}
